import { useEffect, useRef, forwardRef, useImperativeHandle } from "react";

export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
export const captchaEnabled = !!TURNSTILE_SITE_KEY;

// Reusable Cloudflare Turnstile widget (explicit render for SPA).
// Managed mode auto-solves; exposes reset() so a fresh single-use token can be
// generated after each submit attempt.
export const TurnstileWidget = forwardRef(({ onToken, onExpired, className }, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  const onExpiredRef = useRef(onExpired);
  onTokenRef.current = onToken;
  onExpiredRef.current = onExpired;

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current != null && window.turnstile) {
        try { window.turnstile.reset(widgetIdRef.current); } catch { /* noop */ }
      }
    },
  }));

  useEffect(() => {
    if (!captchaEnabled) return undefined;
    let cancelled = false;
    let timer;
    const render = () => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      if (widgetIdRef.current != null) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "light",
        callback: (token) => onTokenRef.current?.(token),
        "expired-callback": () => onExpiredRef.current?.(),
        "error-callback": () => onExpiredRef.current?.(),
      });
    };
    if (window.turnstile) render();
    else timer = setInterval(() => { if (window.turnstile) { clearInterval(timer); render(); } }, 100);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      if (widgetIdRef.current != null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* noop */ }
      }
      widgetIdRef.current = null;
    };
  }, []);

  if (!captchaEnabled) return null;
  return <div ref={containerRef} className={className} data-testid="turnstile-widget" />;
});
